import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import { FaExclamationTriangle, FaUndo, FaClock } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';

/**
 * SuspendedConnections Component
 * 
 * Displays and manages suspended connections for users who have cancelled their subscriptions.
 * Provides functionality to view suspended connections and reactivate them through upgrade.
 * 
 * Features:
 * - Display suspended connections with grace period information
 * - Show time remaining in grace period
 * - Provide upgrade prompts for reactivation
 * - Handle expired connections gracefully
 */
const SuspendedConnections = ({ onUpgradeClick }) => {
  const { user } = useAuth();
  const [suspendedConnections, setSuspendedConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reactivating, setReactivating] = useState(false);

  const loadSuspendedConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      console.log('[SUSPENDED] Loading suspended connections for:', user.email);
      const response = await apiClient.get(`/api/connections/suspended?user_email=${encodeURIComponent(user.email)}`);
      console.log('[SUSPENDED] API response:', response);

      if (response.data && response.data.success) {
        console.log('[SUSPENDED] Suspended connections:', response.data.suspended_connections);
        setSuspendedConnections(response.data.suspended_connections || []);
      } else {
        console.warn('[SUSPENDED] No suspended connections data received:', response.data);
        setSuspendedConnections([]);
      }
    } catch (err) {
      console.error('[SUSPENDED] Failed to load suspended connections:', err);
      console.error('[SUSPENDED] Error response:', err.response);
      console.error('[SUSPENDED] Error status:', err.response?.status);
      console.error('[SUSPENDED] Error data:', err.response?.data);
      setError(`Failed to load suspended connections: ${err.response?.data?.error || err.message}`);
      setSuspendedConnections([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.email) {
      loadSuspendedConnections();
    }
  }, [user, loadSuspendedConnections]);

  const handleReactivateConnections = async () => {
    console.log('[SUSPENDED_CONNECTIONS] Button clicked - handleReactivateConnections called');
    
    if (!user?.email) {
      console.log('[SUSPENDED_CONNECTIONS] No user email found');
      setError('User information not available. Please refresh the page.');
      return;
    }

    console.log('[SUSPENDED_CONNECTIONS] User context:', { 
      accountType: user.accountType, 
      is_trial: user.is_trial,
      email: user.email 
    });

    try {
      setReactivating(true);
      setError('');

      // First check if user has an active paid subscription
      const isTrialUser = !user.accountType || user.accountType === 'trial' || user.accountType === 'Trial' || user.is_trial === 'true';
      
      console.log('[SUSPENDED_CONNECTIONS] Is trial user?', isTrialUser);
      
      if (isTrialUser) {
        console.log('[SUSPENDED_CONNECTIONS] Trial user detected - checking for onUpgradeClick prop');
        console.log('[SUSPENDED_CONNECTIONS] onUpgradeClick function exists?', typeof onUpgradeClick === 'function');
        
        // User needs to upgrade first
        if (onUpgradeClick) {
          console.log('[SUSPENDED_CONNECTIONS] Calling onUpgradeClick()...');
          
          // Show helpful message
          // setError('👆 Please select a plan above to reactivate your connections');
          
          // Call the upgrade handler to scroll/switch tab
          onUpgradeClick();
          console.log('[SUSPENDED_CONNECTIONS] onUpgradeClick() called successfully');
        } else {
          console.log('[SUSPENDED_CONNECTIONS] No onUpgradeClick prop provided');
          setError('Please upgrade to a paid plan first to reactivate your connections.');
        }
        setReactivating(false);
        return;
      }

      // User has paid plan - attempt reactivation
      console.log('[REACTIVATION] Calling unsuspend endpoint with:', {
        user_id: user.user_id,  // Use UUID instead of email
        new_account_type: user.accountType
      });
      
      const response = await apiClient.post('/api/connections/unsuspend', {
        user_id: user.user_id,  // Use UUID instead of email
        new_account_type: user.accountType
      });

      console.log('[REACTIVATION] Response received:', response.data);
      
      if (response.data && response.data.success) {
        const reactivationData = response.data;
        
        if (reactivationData.reactivated_count > 0) {
          // Success - reload suspended connections
          await loadSuspendedConnections();
          
          // Emit event to trigger connection refresh in CalendarConnection
          window.dispatchEvent(new CustomEvent('connection-reactivated', {
            detail: {
              reactivatedCount: reactivationData.reactivated_count,
              accountType: user.accountType
            }
          }));
          
          // Show success message
          setError(''); // Clear any previous errors
          alert(`Successfully reactivated ${reactivationData.reactivated_count} connection(s)! Your connections will appear shortly.`);
        } else {
          setError('No connections were reactivated. They may have already been processed.');
        }
      } else {
        setError(response.data?.error || response.data?.message || 'Failed to reactivate connections.');
      }
    } catch (err) {
      console.error('Connection reactivation failed:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to reactivate connections. Please try again or contact support.');
      }
    } finally {
      setReactivating(false);
    }
  };

  const formatTimeRemaining = (suspensionDate, gracePeriodDays = 30) => {
    console.log('[SUSPENDED] formatTimeRemaining called with:', { suspensionDate, gracePeriodDays, type: typeof suspensionDate });
    
    if (!suspensionDate) {
      console.warn('[SUSPENDED] No suspension date provided - using default "30 days remaining"');
      // Fallback: If suspension date is missing, assume it was just suspended
      // This handles legacy suspended connections that don't have the field
      return `${gracePeriodDays} days remaining (estimated)`;
    }
    
    try {
      const suspended = new Date(suspensionDate);
      console.log('[SUSPENDED] Parsed suspension date:', suspended, 'isValid:', !isNaN(suspended.getTime()));
      
      if (isNaN(suspended.getTime())) {
        console.warn('[SUSPENDED] Invalid date object created from:', suspensionDate);
        return `${gracePeriodDays} days remaining (estimated)`;
      }
      
      const expiry = new Date(suspended.getTime() + (gracePeriodDays * 24 * 60 * 60 * 1000));
      const now = new Date();
      const timeLeft = expiry.getTime() - now.getTime();
      
      console.log('[SUSPENDED] Time calculation:', { expiry, now, timeLeft, daysLeft: Math.floor(timeLeft / (24 * 60 * 60 * 1000)) });
      
      if (timeLeft <= 0) {
        return 'Expired';
      }
      
      const daysLeft = Math.floor(timeLeft / (24 * 60 * 60 * 1000));
      const hoursLeft = Math.floor((timeLeft % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      
      if (daysLeft > 0) {
        return `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`;
      } else {
        return `${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''} remaining`;
      }
    } catch (err) {
      console.warn('[SUSPENDED] Error calculating time remaining:', err);
      return `${gracePeriodDays} days remaining (estimated)`;
    }
  };

  const getStatusBadge = (suspensionDate, gracePeriodDays = 30) => {
    const timeRemaining = formatTimeRemaining(suspensionDate, gracePeriodDays);
    
    if (timeRemaining === 'Expired') {
      return <Badge bg="danger">Expired</Badge>;
    } else if (timeRemaining.includes('hour')) {
      return <Badge bg="warning">Expiring Soon</Badge>;
    } else {
      return <Badge bg="secondary">Suspended</Badge>;
    }
  };

  // Don't show if no user
  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Header>
          <h5><FaExclamationTriangle className="me-2" />Suspended Connections</h5>
        </Card.Header>
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading suspended connections...
        </Card.Body>
      </Card>
    );
  }

  if (suspendedConnections.length === 0) {
    return null; // No suspended connections to show
  }

  return (
    <Card className="mb-4">
      <Card.Header>
        <h6><FaExclamationTriangle className="me-2 text-warning" />Suspended Connections</h6>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant={error.includes('👆') ? 'info' : 'danger'} className="mb-3">
            {error}
          </Alert>
        )}
        
        <Alert variant="warning" className="mb-3 small">
          <h6><FaClock className="me-2" />Grace Period Active</h6>
          <p className="mb-2">
            Your connections have been suspended due to subscription cancellation. 
            You can reactivate them by upgrading to a paid plan.
          </p>
          <p className="mb-0">
            <strong>Important:</strong> Connections will be permanently removed after the grace period expires.
          </p>
        </Alert>

        {suspendedConnections.length > 0 && (
          <div className="small">
            <Table striped bordered hover responsive className="mb-2">
              <thead>
                <tr>
                  <th>Connection Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Grace Period</th>
                  <th>Original Plan</th>
                </tr>
              </thead>
              <tbody>
                {suspendedConnections.map((conn, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{conn.connection_name || `Connection ${index + 1}`}</strong>
                      {conn.company_name && (
                        <div className="text-muted small">{conn.company_name}</div>
                      )}
                    </td>
                    <td>
                      <Badge bg="info" className="text-capitalize">
                        {conn.connection_type || 'Unknown'}
                      </Badge>
                    </td>
                    <td>
                      {getStatusBadge(conn.suspension_date, conn.grace_period_days)}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaClock className="me-1 text-muted" />
                        {formatTimeRemaining(conn.suspension_date, conn.grace_period_days)}
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary" className="text-capitalize">
                        {conn.original_account_type || 'Unknown'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="text-center">
              <Button
                variant="success"
                size="md"
                onClick={handleReactivateConnections}
                disabled={reactivating}
              >
                {reactivating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaUndo className="me-1" />
                    Upgrade to Reactivate Connections
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SuspendedConnections;